import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  LabelList,
} from "recharts";
import { getThresholdsConfig } from "../../services/api";
import { buildVehicleTrend } from "../../services/trendScoring";
import { CANVAS_THEME } from "../../services/thresholdUtils";
import { useTheme } from "../../App";
import useIsMobile from "../../hooks/useIsMobile";
import { statusLabel } from "../../services/metricDefinitions";

const PURCHASE_URL = "https://www.speediagnostix.com/collections/oil-analysis-kit";
const STATUS_SYMBOLS = {
  normal: { src: "/images/status/normal-thumbs-up.png", scale: 1.5 },
  warn: { src: "/images/status/warning-so-so.png", scale: 1.65 },
  critical: { src: "/images/status/critical-thumbs-down.png", scale: 1.8 },
};

const s = {
  panel: {
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "var(--dashboard-card-radius)",
    boxShadow: "var(--dashboard-card-shadow)",
    padding: 18,
    marginBottom: 20,
  },
  disabledPanel: {
    background: "var(--bg-card-subtle)",
    border: "1px dashed var(--border)",
    borderRadius: "var(--dashboard-card-radius)",
    padding: 24,
    marginBottom: 20,
    textAlign: "center",
    color: "var(--text-muted)",
  },
  reportRail: { marginBottom: 14 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 12 },
  eyebrow: { fontSize: 11, fontWeight: 800, color: "var(--teal-dark)", letterSpacing: "0.08em", textTransform: "uppercase" },
  title: { fontFamily: "var(--font-heading)", fontSize: 22, fontWeight: 800, color: "var(--text-primary)", marginTop: 4 },
  subtitle: { fontSize: 13, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.45 },
  selector: { display: "flex", gap: 6, background: "var(--dashboard-tab-bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 4 },
  pill: {
    border: "none",
    borderRadius: 6,
    padding: "8px 11px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  scoreStrip: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 10 },
  scoreCard: { border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", minHeight: 96, boxSizing: "border-box", background: "var(--bg-card-subtle)", position: "relative", overflow: "hidden" },
  scoreLabel: { fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" },
  scoreStatus: { fontFamily: "var(--font-heading)", fontSize: 22, fontWeight: 900, marginTop: 5, textTransform: "uppercase" },
  statusSymbol: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 48, height: 48, objectFit: "contain", filter: "drop-shadow(0 8px 14px rgba(15, 23, 42, 0.18))" },
  chartWrap: { width: "100%", height: 252 },
  alertStack: { display: "grid", gap: 8, marginTop: 12 },
  alertCard: { borderRadius: 8, padding: "11px 13px", border: "1.5px solid", fontSize: 13, lineHeight: 1.45 },
  alertTitle: { fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 },
  detailsToggle: {
    marginTop: 12,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-secondary)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  metricGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 12 },
  metricCard: { border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 12, background: "var(--bg-card-subtle)" },
  metricName: { fontSize: 12, fontWeight: 800, color: "var(--text-primary)" },
  metricMeta: { fontSize: 11, color: "var(--text-muted)", marginTop: 5 },
  purchase: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
    padding: "10px 18px",
    background: "var(--teal-dark)",
    color: "#fff",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 800,
  },
  noteCard: {
    display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap",
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: "var(--dashboard-card-radius)", boxShadow: "var(--dashboard-card-shadow)",
    margin: "18px 0 0", padding: "18px 20px", textAlign: "left",
  },
  noteLogo: { width: 250, maxWidth: "100%", height: "auto", flex: "0 0 250px" },
  noteBody: { flex: "1 1 320px", minWidth: 0 },
  noteTitle: { fontSize: 16, fontWeight: 900, color: "var(--teal-dark)", marginBottom: 8 },
  noteText: { fontSize: 14, lineHeight: 1.65, color: "var(--text-primary)", whiteSpace: "pre-line" },
};

function formatDate(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
}

function formatValue(value) {
  if (value === null || value === undefined) return "--";
  return `${Math.round(value)}`;
}

function formatRawValue(value) {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value);
}

function riskStatus(score) {
  if (score === null || score === undefined) return "na";
  if (score >= 90) return "critical";
  if (score >= 50) return "warn";
  return "normal";
}

function riskLabel(score, engineMileage = null) {
  const status = riskStatus(score);
  if (status !== "na") return statusLabel(status, engineMileage, "Warning");
  return "No score";
}

function riskColor(score, isDark) {
  const colors = CANVAS_THEME[isDark ? "dark" : "light"];
  return colors[riskStatus(score)] || colors.na;
}

function riskRank(score) {
  const status = riskStatus(score);
  if (status === "critical") return 3;
  if (status === "warn") return 2;
  if (status === "normal") return 1;
  return 0;
}

function statusSymbolFor(score) {
  return STATUS_SYMBOLS[riskStatus(score)] || null;
}

export function OilGeekNote({ note }) {
  if (!note) return null;
  return (
    <section style={s.noteCard}>
      <img src="/images/motor-oil-geek.png" alt="The Motor Oil Geek" style={s.noteLogo} />
      <div style={s.noteBody}>
        <div style={s.noteTitle}>Note from The Motor Oil Geek:</div>
        <div style={s.noteText}>{note}</div>
      </div>
    </section>
  );
}

function CategoryScoreStrip({ trend, selectedPoint, activeCategory, selectCategory, isMobile, isDark }) {
  return (
    <div style={{ ...s.scoreStrip, gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))" }}>
      {trend.categories.map((category) => {
        const active = activeCategory === category.id;
        const displayScore = selectedPoint?.[category.id] ?? category.latestScore;
        const color = riskColor(displayScore, isDark);
        const symbol = statusSymbolFor(displayScore);
        const symbolSize = symbol ? 48 * symbol.scale : 36;
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => selectCategory(category.id)}
            style={{
              ...s.scoreCard,
              paddingRight: isMobile ? Math.max(66, symbolSize + 28) : Math.max(76, symbolSize + 32),
              textAlign: "left",
              cursor: "pointer",
              borderColor: active ? color : "var(--border)",
              boxShadow: active ? `0 0 0 2px ${color}22` : "none",
            }}
          >
            <div style={s.scoreLabel}>{category.label}</div>
            <div style={{ ...s.scoreStatus, color }}>{riskLabel(displayScore, selectedPoint?.engineMiles)}</div>
            {symbol ? (
              <img
                src={symbol.src}
                alt=""
                aria-hidden="true"
                style={{
                  ...s.statusSymbol,
                  width: symbolSize,
                  height: symbolSize,
                }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  ...s.statusSymbol,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: color,
                  boxShadow: `0 0 0 7px ${color}18, 0 0 18px ${color}88`,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function alertBackground(status) {
  if (status === "critical") return "var(--status-critical-bg, #fee2e2)";
  if (status === "warn") return "var(--status-warn-bg, #fef3c7)";
  return "var(--bg-card-subtle)";
}

function categoryNoun(categoryLabel) {
  return categoryLabel === "Oil Condition" ? "Oil Condition" : `${categoryLabel}`;
}

function thresholdRangeText(contributor) {
  const threshold = contributor?.threshold;
  if (contributor?.kind === "bool") return "acceptable result is negative / not detected";
  if (!threshold) return "no published threshold range is available";

  const warn = Number(threshold.warn);
  const fail = Number(threshold.fail);
  const pass = Number(threshold.pass);
  const unit = contributor.unit ? ` ${contributor.unit}` : "";

  if (Number.isFinite(pass) && pass > 0 && Number.isFinite(warn) && warn > 0 && (!Number.isFinite(fail) || fail === 0)) {
    return `normal range is ${warn}${unit} to ${pass}${unit}`;
  }

  if (Number.isFinite(warn) && warn > 0 && Number.isFinite(fail) && fail > warn) {
    return `normal is below ${warn}${unit}; warning is ${warn}${unit} to below ${fail}${unit}; critical is ${fail}${unit} or higher`;
  }

  if (Number.isFinite(warn) && warn > 0) {
    return `normal is below ${warn}${unit}`;
  }

  return "no published threshold range is available";
}

function formatContributorValue(contributor) {
  if (!contributor) return "--";
  if (contributor.kind === "bool") {
    return String(contributor.value || "").toUpperCase().startsWith("P") ? "Positive / detected" : formatRawValue(contributor.value);
  }
  const numeric = Number(contributor.value);
  const formatted = Number.isFinite(numeric)
    ? numeric.toLocaleString(undefined, { maximumFractionDigits: contributor.precision ?? 2 })
    : formatRawValue(contributor.value);
  return contributor.unit ? `${formatted} ${contributor.unit}` : formatted;
}

function gradientId(categoryId) {
  return `trend-gradient-${categoryId}`;
}

function boundaryStopsBetween(start, end, isDark) {
  const startScore = Number(start.score);
  const endScore = Number(end.score);
  if (!Number.isFinite(startScore) || !Number.isFinite(endScore) || startScore === endScore) return [];

  return [50, 90]
    .filter((boundary) => boundary > Math.min(startScore, endScore) && boundary < Math.max(startScore, endScore))
    .map((boundary) => ({
      boundary,
      t: (boundary - startScore) / (endScore - startScore),
    }))
    .sort((a, b) => a.t - b.t)
    .map(({ boundary, t }) => ({
      offset: start.offset + (end.offset - start.offset) * t,
      color: riskColor(boundary, isDark),
    }));
}

function gradientStops(points, category, isDark) {
  const available = points
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => point[category.id] !== null && point[category.id] !== undefined);
  if (available.length === 0) return [];
  if (available.length === 1) {
    return [{ offset: 0, color: riskColor(available[0].point[category.id], isDark) }];
  }
  const lastIndex = Math.max(points.length - 1, 1);
  const anchors = available.map(({ point, index }) => {
    const score = point[category.id];
    return {
      offset: (index / lastIndex) * 100,
      score,
      color: point[`${category.id}Color`] || riskColor(score, isDark),
    };
  });
  const stops = [anchors[0]];
  anchors.slice(1).forEach((anchor) => {
    const previous = stops[stops.length - 1];
    stops.push(...boundaryStopsBetween(previous, anchor, isDark));
    stops.push(anchor);
  });
  return stops.map((stop) => ({
    offset: Math.max(0, Math.min(100, Number(stop.offset.toFixed(2)))),
    color: stop.color,
  }));
}

function lineStroke(points, category, isDark) {
  const available = points.filter((point) => point[category.id] !== null && point[category.id] !== undefined);
  const latest = available[0];
  const latestColor = latest?.[`${category.id}Color`] || riskColor(latest?.[category.id], isDark);
  const statuses = new Set(available.map((point) => riskStatus(point[category.id])));
  const values = new Set(available.map((point) => point[category.id]));

  if (available.length <= 2 || statuses.size <= 1 || values.size <= 1) {
    return latestColor;
  }

  return `url(#${gradientId(category.id)})`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const rows = payload
    .filter((entry) => entry.value !== null && entry.value !== undefined)
    .sort((a, b) => b.value - a.value);

  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: 12, boxShadow: "var(--dashboard-card-shadow)" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-primary)", marginBottom: 6 }}>{formatDate(point.date || label)}</div>
      {(point.engineMiles || point.sampleMiles) && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
          {point.engineMiles ? `Engine miles: ${point.engineMiles}` : ""}
          {point.engineMiles && point.sampleMiles ? " | " : ""}
          {point.sampleMiles ? `Oil miles: ${point.sampleMiles}` : ""}
        </div>
      )}
      {rows.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 18, fontSize: 12, marginTop: 4 }}>
          <span style={{ color: entry.payload?.[`${entry.dataKey}Color`] || entry.color, fontWeight: 800 }}>{entry.name}</span>
          <span style={{ color: "var(--text-primary)", fontFamily: "var(--font-mono)", textAlign: "right" }}>
            {riskLabel(entry.payload?.[`${entry.dataKey}RiskScore`] ?? entry.value, point.engineMiles)}
            {entry.payload?.[`${entry.dataKey}TrendMetric`] && (
              <span style={{ display: "block", color: "var(--text-muted)", fontFamily: "inherit", marginTop: 2 }}>
                {entry.payload[`${entry.dataKey}TrendMetric`]}: {formatRawValue(entry.payload[`${entry.dataKey}TrendRawValue`])}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function ActiveDateTick({ x, y, payload, activeX, points }) {
  const point = points.find((item) => item.trendX === payload?.value);
  const isActive = payload?.value !== undefined && payload.value === activeX;
  return (
    <text
      x={x}
      y={y + (isActive ? 18 : 14)}
      textAnchor="middle"
      fill={isActive ? "var(--text-primary)" : "var(--text-muted)"}
      fontSize={isActive ? 14 : 11}
      fontWeight={isActive ? 900 : 500}
    >
      {formatDate(point?.date)}
    </text>
  );
}

function ActiveValueLabel({ x, y, value, payload, dataKey, activeCategory }) {
  if (!payload?.isSelectedReport || value === null || value === undefined) return null;
  const color = payload?.[`${dataKey}Color`] || "var(--text-primary)";
  const isActiveCategory = dataKey === activeCategory;
  return (
    <text
      x={x + 9}
      y={y - 9}
      fill={color}
      fontSize={isActiveCategory ? 16 : 13}
      fontWeight={900}
      opacity={isActiveCategory ? 1 : 0.72}
      paintOrder="stroke"
      stroke="var(--bg-card)"
      strokeWidth={4}
      style={{ pointerEvents: "none" }}
    >
      {formatValue(value)}
    </text>
  );
}

function AlertExplanation({ alerts, isDark, engineMileage }) {
  if (!alerts.length) return null;

  return (
    <div style={s.alertStack}>
      {alerts.map((alert) => {
        const color = riskColor(alert.score, isDark);
        return (
          <div
            key={`${alert.categoryId}-${alert.key}`}
            style={{
              ...s.alertCard,
              borderColor: color,
              background: alertBackground(riskStatus(alert.score)),
              color: "var(--text-primary)",
            }}
          >
            <div style={{ ...s.alertTitle, color }}>
              {riskLabel(alert.score, engineMileage)} {alert.categoryLabel}
            </div>
            <div>
              The {alert.label} level in {categoryNoun(alert.categoryLabel)} is in {riskLabel(alert.score, engineMileage).toLowerCase()}.
              {" "}The acceptable range for {alert.label} is: {thresholdRangeText(alert)}.{" "}
              <strong>YOUR LEVEL IS {formatContributorValue(alert).toUpperCase()}.</strong>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function VehicleTrendHero({
  vehicleGroup,
  selectedSample,
  selectedThresholds = null,
  reportSelector = null,
  activeCategory: controlledActiveCategory,
  onCategorySelect,
}) {
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const [thresholdConfig, setThresholdConfig] = useState(null);
  const [internalActiveCategory, setInternalActiveCategory] = useState("contamination");
  const [showDetails, setShowDetails] = useState(false);
  const lastAutoFocusKey = useRef("");
  const activeCategory = controlledActiveCategory || internalActiveCategory;
  const selectCategory = useCallback((categoryId) => {
    setInternalActiveCategory(categoryId);
    onCategorySelect?.(categoryId);
  }, [onCategorySelect]);

  useEffect(() => {
    let alive = true;
    getThresholdsConfig()
      .then((res) => {
        if (alive) setThresholdConfig(res.data);
      })
      .catch(() => {
        if (alive) setThresholdConfig({});
      });
    return () => { alive = false; };
  }, []);

  const selectedSampleId = selectedSample?.lab_sample_id;
  const trend = useMemo(
    () => buildVehicleTrend(
      vehicleGroup?.readySamples || [],
      thresholdConfig || {},
      selectedSampleId && selectedThresholds ? { [selectedSampleId]: selectedThresholds } : {}
    ),
    [vehicleGroup?.readySamples, thresholdConfig, selectedSampleId, selectedThresholds]
  );

  const activeMeta = trend.categories.find((category) => category.id === activeCategory) || trend.categories[0];
  const lineData = trend.points.map((point, index) => {
    const next = { ...point, trendX: index, dateLabel: formatDate(point.date) };
    next.isSelectedReport = point.sampleId === selectedSampleId;
    trend.categories.forEach((category) => {
      const topContributor = point[`${category.id}Contributors`]?.[0];
      next[`${category.id}RiskScore`] = point[category.id];
      next[category.id] = point[category.id];
      next[`${category.id}Color`] = riskColor(point[category.id], isDark);
      if (topContributor) {
        next[`${category.id}TrendMetric`] = topContributor.label;
        next[`${category.id}TrendRawValue`] = topContributor.value;
      }
    });
    return next;
  });
  const selectedPoint = lineData.find((point) => point.isSelectedReport);
  const hasPendingReport = (vehicleGroup?.samples || []).some((sample) =>
    String(sample?.has_test_measurements || "").toUpperCase() !== "YES"
  );
  const selectedAlerts = useMemo(() => {
    if (!selectedPoint) return [];
    return trend.categories
      .flatMap((category) => (selectedPoint[`${category.id}Contributors`] || [])
        .filter((contributor) => riskStatus(contributor.score) === "warn" || riskStatus(contributor.score) === "critical")
        .map((contributor) => ({
          ...contributor,
          categoryId: category.id,
          categoryLabel: category.label,
        })))
      .sort((a, b) => b.score - a.score);
  }, [selectedPoint, trend.categories]);
  const gridColor = isDark ? "#263244" : "#e2e8f0";
  const axisColor = isDark ? "#94a3b8" : "#64748b";
  const selectedMarkerColor = isDark ? "#f8fafc" : "#0f172a";

  useEffect(() => {
    if (!selectedPoint) return;
    const categoryScores = trend.categories.map((category) => ({
      id: category.id,
      score: selectedPoint[category.id],
    }));
    const autoFocusKey = [
      vehicleGroup?.id || "",
      selectedPoint.sampleId || "",
      ...categoryScores.map((category) => `${category.id}:${category.score ?? "na"}`),
    ].join(":");
    if (lastAutoFocusKey.current === autoFocusKey) return;
    const priorityCategory = categoryScores
      .filter((category) => riskRank(category.score) >= 2)
      .sort((a, b) => riskRank(b.score) - riskRank(a.score) || b.score - a.score)[0];
    if (!priorityCategory) {
      lastAutoFocusKey.current = autoFocusKey;
      return;
    }
    lastAutoFocusKey.current = autoFocusKey;
    selectCategory(priorityCategory.id);
  }, [selectCategory, selectedPoint, trend.categories, vehicleGroup?.id]);

  if (!trend.isActive) {
    return (
      <section style={s.panel}>
        {reportSelector && <div style={s.reportRail}>{reportSelector}</div>}
        <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row" }}>
          <div>
            <div style={s.eyebrow}>Trend Analysis</div>
            <div style={s.title}>Current Report Health</div>
            <div style={s.subtitle}>Threshold-aware status from this completed oil report.</div>
          </div>
        </div>

        <CategoryScoreStrip
          trend={trend}
          selectedPoint={selectedPoint}
          activeCategory={activeCategory}
          selectCategory={selectCategory}
          isMobile={isMobile}
          isDark={isDark}
        />

        <div style={{ ...s.disabledPanel, opacity: 0.9, marginBottom: 0 }}>
          <div style={{ ...s.title, color: "var(--text-secondary)", marginTop: 0 }}>
            {hasPendingReport ? "Pending report will update trends" : "More reports unlock vehicle trends"}
          </div>
          <div style={{ maxWidth: 620, margin: "10px auto 0", fontSize: 14, lineHeight: 1.55 }}>
            {hasPendingReport
              ? "You have a pending oil analysis report. Check back soon to see an updated trend analysis once your report is processed."
              : "This vehicle only has only one oil report to date. Purchase another oil report in order to activate our trend analysis."}
          </div>
          {!hasPendingReport && <a href={PURCHASE_URL} target="_blank" rel="noopener noreferrer" style={s.purchase}>Purchase now</a>}
        </div>
      </section>
    );
  }

  return (
    <section style={s.panel}>
      {reportSelector && <div style={s.reportRail}>{reportSelector}</div>}

      <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row" }}>
        <div>
          <div style={s.eyebrow}>Trend Analysis</div>
          <div style={s.title}>Vehicle Health Trend</div>
          <div style={s.subtitle}>
            Threshold-aware risk score across {trend.readySamples.length} completed reports. Higher scores mean more attention is needed.
          </div>
        </div>
        <div style={{ ...s.selector, width: isMobile ? "100%" : "auto" }}>
          {trend.categories.map((category) => {
            const active = activeCategory === category.id;
            const displayScore = selectedPoint?.[category.id] ?? category.latestScore;
            const color = riskColor(displayScore, isDark);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => selectCategory(category.id)}
                style={{
                  ...s.pill,
                  flex: isMobile ? 1 : "initial",
                  background: active ? color : "transparent",
                  color: active ? "#fff" : "var(--text-secondary)",
                }}
              >
                {isMobile ? category.shortLabel : category.label}
              </button>
            );
          })}
        </div>
      </div>

      <CategoryScoreStrip
        trend={trend}
        selectedPoint={selectedPoint}
        activeCategory={activeCategory}
        selectCategory={selectCategory}
        isMobile={isMobile}
        isDark={isDark}
      />

      <div style={s.chartWrap}>
        <ResponsiveContainer>
          <LineChart data={lineData} margin={{ top: 12, right: isMobile ? 18 : 28, left: isMobile ? -12 : 0, bottom: 4 }}>
            <defs>
              {trend.categories.map((category) => (
                <linearGradient key={category.id} id={gradientId(category.id)} x1="0%" y1="0%" x2="100%" y2="0%">
                  {gradientStops(lineData, category, isDark).map((stop, index) => (
                    <stop key={`${stop.offset}-${index}`} offset={`${stop.offset}%`} stopColor={stop.color} />
                  ))}
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="trendX"
              type="number"
              domain={[0, Math.max(lineData.length - 1, 0)]}
              ticks={lineData.map((point) => point.trendX)}
              tick={<ActiveDateTick activeX={selectedPoint?.trendX} points={lineData} />}
              height={38}
              interval={0}
              allowDecimals={false}
            />
            <YAxis domain={[0, 100]} tick={{ fill: axisColor, fontSize: 11 }} />
            {selectedPoint && (
              <ReferenceLine
                x={selectedPoint.trendX}
                stroke={selectedMarkerColor}
                strokeWidth={3}
                strokeOpacity={0.78}
                ifOverflow="extendDomain"
              />
            )}
            <Tooltip content={<CustomTooltip />} />
            {!isMobile && (
              <Legend
                formatter={(value) => (
                  <span style={{ color: "var(--text-primary)", fontWeight: 700 }}>{value}</span>
                )}
              />
            )}
            {trend.categories.map((category) => (
              <Line
                key={category.id}
                type="linear"
                dataKey={category.id}
                name={category.label}
                stroke={lineStroke(lineData, category, isDark)}
                strokeWidth={activeCategory === category.id ? 4 : 2}
                strokeOpacity={activeCategory === category.id ? 1 : 0.38}
                style={{ cursor: "pointer" }}
                onClick={() => selectCategory(category.id)}
                dot={(props) => {
                  const color = props.payload?.[`${category.id}Color`] || riskColor(props.value, isDark);
                  const isSelectedReport = props.payload?.isSelectedReport;
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={isSelectedReport ? (activeCategory === category.id ? 7 : 5) : (activeCategory === category.id ? 4 : 2)}
                      fill={color}
                      stroke={isSelectedReport ? "var(--bg-card)" : "none"}
                      strokeWidth={isSelectedReport ? 2 : 0}
                      opacity={isSelectedReport || activeCategory === category.id ? 1 : 0.32}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectCategory(category.id)}
                    />
                  );
                }}
                activeDot={(props) => {
                  const color = props.payload?.[`${category.id}Color`] || riskColor(props.value, isDark);
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectCategory(category.id)}
                    />
                  );
                }}
                connectNulls
              >
                <LabelList
                  dataKey={category.id}
                  content={(props) => (
                    <ActiveValueLabel {...props} dataKey={category.id} activeCategory={activeCategory} />
                  )}
                />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <AlertExplanation alerts={selectedAlerts} isDark={isDark} engineMileage={selectedPoint?.engineMiles} />

      <button type="button" style={s.detailsToggle} onClick={() => setShowDetails(!showDetails)}>
        {showDetails ? "Hide" : "Show"} individual measurement trends
      </button>

      {showDetails && (
        <div style={s.metricGrid}>
          {activeMeta.rawMetrics.length === 0 ? (
            <div style={{ ...s.metricCard, color: "var(--text-muted)", fontSize: 13 }}>No repeated raw measurements are available for this category yet.</div>
          ) : activeMeta.rawMetrics.slice(0, 8).map((metric) => {
            const first = metric.values[0]?.value;
            const last = metric.values[metric.values.length - 1]?.value;
            return (
              <div key={metric.key} style={s.metricCard}>
                <div style={s.metricName}>{metric.label}</div>
                <div style={s.metricMeta}>
                  Newest to oldest: {String(first)}{metric.unit ? ` ${metric.unit}` : ""} to {String(last)}{metric.unit ? ` ${metric.unit}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
